import { useMemo, useState } from "react";
import { buildPeriodPresets, pickableMonths } from "../lib/scenario";
import { fmtMonthShort } from "../lib/format";
import { ChevronDown } from "lucide-react";

// Period selector — driven by real "YYYY-MM" keys. Presets are rolling
// (always anchored on today); custom range can reach back into stored past
// months or forward beyond the current stored window.
export default function PeriodSelector({ allMonths, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("preset");
  const presets = useMemo(() => buildPeriodPresets(allMonths || []), [allMonths]);
  const pickable = useMemo(() => pickableMonths(allMonths || []), [allMonths]);
  const [from, setFrom] = useState(value?.[0] || pickable[0] || "");
  const [to, setTo] = useState(value?.[value.length - 1] || pickable[pickable.length - 1] || "");

  const activeLabel = (() => {
    if (!value || !value.length) return "All months";
    if (value.length === 1) return fmtMonthShort(value[0]);
    return `${fmtMonthShort(value[0])} – ${fmtMonthShort(value[value.length - 1])}`;
  })();

  const choosePreset = (preset) => { onChange(preset.months); setOpen(false); };

  const applyCustom = () => {
    const i0 = pickable.indexOf(from);
    const i1 = pickable.indexOf(to);
    if (i0 === -1 || i1 === -1) return;
    const [a, b] = i0 <= i1 ? [i0, i1] : [i1, i0];
    onChange(pickable.slice(a, b + 1));
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="period-selector">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] bg-white text-[11px] font-display tracked-tight uppercase text-[var(--ink)] hover:bg-[var(--paper-soft)]"
        data-testid="period-button"
      >
        <span className="eyebrow normal-case tracking-normal text-[var(--soft-fg)]">Period</span>
        <span className="font-mono-num normal-case tracking-normal">{activeLabel}</span>
        <ChevronDown size={12} />
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-[var(--rule-strong)] shadow-sm animate-fade-up" onMouseLeave={() => setOpen(false)}>
          <div className="flex border-b border-[var(--rule)]">
            <button
              onClick={() => setMode("preset")}
              className={`flex-1 py-2 eyebrow ${mode === "preset" ? "bg-[var(--ink)] text-white" : "text-[var(--muted-fg)]"}`}
              data-testid="period-mode-preset"
            >Presets</button>
            <button
              onClick={() => setMode("custom")}
              className={`flex-1 py-2 eyebrow ${mode === "custom" ? "bg-[var(--ink)] text-white" : "text-[var(--muted-fg)]"}`}
              data-testid="period-mode-custom"
            >Custom range</button>
          </div>

          {mode === "preset" ? (
            <ul className="p-2">
              {presets.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => choosePreset(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--paper-soft)] font-mono-num"
                    data-testid={`period-preset-${p.id}`}
                  >{p.label}</button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 space-y-3">
              <label className="block">
                <span className="eyebrow">From</span>
                <select value={from} onChange={(e) => setFrom(e.target.value)}
                  className="w-full mt-1 border border-[var(--rule)] p-2 text-sm font-mono-num"
                  data-testid="period-custom-from">
                  {pickable.map((m) => <option key={m} value={m}>{fmtMonthShort(m)}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">To</span>
                <select value={to} onChange={(e) => setTo(e.target.value)}
                  className="w-full mt-1 border border-[var(--rule)] p-2 text-sm font-mono-num"
                  data-testid="period-custom-to">
                  {pickable.map((m) => <option key={m} value={m}>{fmtMonthShort(m)}</option>)}
                </select>
              </label>
              <button
                onClick={applyCustom}
                className="w-full bg-[var(--ink)] text-white py-2 text-xs font-display tracked-tight uppercase"
                data-testid="period-custom-apply"
              >Apply</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
